import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode, type PointerEvent as ReactPointerEvent } from "react";
import type { LibraryCompletedEngagement, LibraryOpenEngagement, LibraryState, RubricDimension } from "@pushback-and-chaos/contracts";
import { CASEBOOK, LIBRARY } from "../design/copy.js";
import { PanelHeading } from "./storyteller/chrome.js";
import { endingByline } from "./storyteller/casebook-atoms.js";
import { CardRows, CaseKindRow, CasePaperCover, CoverSprites, caseKindWords } from "../ui/case-card.js";
import { CaseLightbox, DRAG_Z, LEAVE_MS, lightboxPositionFor, StartOverConfirm, type LightboxState } from "../ui/case-lightbox.js";
import { usePointerDrag } from "../ui/use-drag.js";
import { buildShelves, lockedMessageFor, type Shelf, type ShelfCase } from "./library-shelves.js";

/**
 * THE CASE LIBRARY (library pass, 2026-09-08) — shelves you browse sideways.
 *
 * The earlier cut argued for three rows and no sideways scroll on the
 * grounds that there would only ever be a handful of cases. The reference
 * frames for this pass ask for the other thing on purpose: rows of cards
 * organised the way a streaming shelf is — by track, by the skill a case
 * trains, by what is bearing down on the client — each row scrolling on its
 * own, the whole page reading as a wall of covers. What is kept from the old
 * argument: a locked row is never a padlock, it is a sentence saying what
 * opens it.
 *
 * THE CARD ENGINE is the desk's (documents/desk.tsx), applied here. A card is
 * a real <button>: a press that never moves opens it; a press that moves
 * drags it — position: fixed so it can leave its shelf — and the drop
 * decides what the gesture meant. Dropped on the Saved box it shrinks to a
 * keepsake and stays there across sessions (the server holds the list);
 * dropped anywhere else it goes back to its slot. Enter or Space on a
 * focused card opens it the same way a click does; the gesture is never the
 * only door.
 *
 * ONE LIGHTBOX AT A TIME. Opening a card expands it beside where it was;
 * opening another minimises the first back down as the second comes up. The
 * lightbox drags by its header and stays where it is put for as long as it
 * is open.
 *
 * DRAWN AS ONE SHEET (2026-09-22): the page takes the Storyteller's
 * vocabulary — `.pc-sheet` carries the play surface's tokens — so the rail
 * is the desk's paper behind a hairline, every box and every shelf is
 * introduced by the same small tracked label over a rule that heads the
 * panels on the front door and in play, and the cards are the only objects
 * with a shadow. Nothing in the engine below changed.
 *
 * WHAT A CARD SAYS: the client's name, and three rows — track, goal, skill —
 * which are the bible's track, pressure context and primary dimension in
 * words. The cover is one Ink Stroke card painting per case (design/covers.ts),
 * a plated symbol drawn through <use>, its painting served as a
 * content-addressed /plates/ file.
 *
 * THE TWO BARS on a Continue card are unlabelled: how much of the
 * engagement's calendar has gone by, and how much of the evidence has been
 * found. Their only words are the tooltip's — "six days to go." — the form
 * Law 2 sanctions for the day budget. No numeral is rendered on the card.
 */

/** One published case as the library shows it. The opening may still be loading, so two fields can be null. */
export interface LibraryCase extends ShelfCase {
  readonly engagementQuestion: string | null;
  readonly industry: string | null;
  /** The card's lede in the lightbox — authored tension line, or the engagement question. */
  readonly tension: string;
}

export interface CaseLibraryProps {
  readonly cases: readonly LibraryCase[];
  /** Null while the learner's shelf state is still arriving; the shelves render locked until it is known. */
  readonly state: LibraryState | null;
  /** The record's lagging dimension and the plan's dimensions, for the coach's row. */
  readonly lagging: RubricDimension | null;
  readonly planDimensions: readonly RubricDimension[];
  readonly busy: boolean;
  readonly onContinue: (sessionId: string, caseSlug: string) => void;
  /** Open the case. `replaceOpen` is only ever true after the learner has confirmed throwing away their open run. */
  readonly onStart: (slug: string, replaceOpen: boolean) => void;
  readonly onReadPrologue: (slug: string) => void;
  /** The saved box, replaced whole in the learner's order. */
  readonly onSetSaved: (slugs: readonly string[]) => void;
  /** A Completed keepsake's door: that engagement's casebook (2026-09-24). */
  readonly onReadBook?: ((sessionId: string) => void) | undefined;
}

/** Card geometry, in rem: the reference frames' 270 x 505 at 2000 wide. */
const CARD_W_REM = 17;

interface Point {
  readonly x: number;
  readonly y: number;
}

/** The element under a drop point, when the platform can tell us (jsdom cannot). */
function dropTargetAt(x: number, y: number, selector: string): Element | null {
  if (typeof document.elementFromPoint !== "function") return null;
  return document.elementFromPoint(x, y)?.closest(selector) ?? null;
}

/**
 * A bar with no words on the face. Its label is the accessible name, and it
 * appears as a caption under the bar while the pointer rests on it — so the
 * face stays clean and the sentence (in the day-budget form Law 2 allows)
 * is one hover away. `tone` is the fill: the environment's maroon string
 * for time running down, the link blue for evidence building up.
 */
function Bar({ fraction, label, tone }: { fraction: number; label: string; tone: "time" | "evidence" }): ReactNode {
  const [hover, setHover] = useState(false);
  const pct = Math.round(Math.min(Math.max(fraction, 0), 1) * 1000) / 10;
  return (
    <span
      className="block"
      onMouseEnter={() => {
        setHover(true);
      }}
      onMouseLeave={() => {
        setHover(false);
      }}
    >
      <span role="img" aria-label={label} className="pc-case-bar">
        <span className={tone === "time" ? "bg-string" : "bg-link"} style={{ width: `${String(pct)}%` }} data-testid="bar-fill" data-tone={tone} />
      </span>
      {hover ? (
        <span aria-hidden="true" className="pc-lightbox-in mt-1 block font-sans text-micro leading-[1.2] text-ink-soft" data-testid="bar-caption">
          {label}
        </span>
      ) : null}
    </span>
  );
}

function LibraryCard({
  entry,
  locked,
  onOpen,
  onSave,
  onDragState,
}: {
  entry: LibraryCase;
  locked: boolean;
  onOpen: (rect: DOMRect) => void;
  onSave: () => void;
  onDragState: (dragging: boolean) => void;
}): ReactNode {
  const [drag, setDrag] = useState<Point | null>(null);
  const grab = useRef<{ offset: Point; width: number; height: number }>({ offset: { x: 0, y: 0 }, width: 0, height: 0 });
  const ref = useRef<HTMLButtonElement | null>(null);

  const startDrag = usePointerDrag({
    onStart: () => {
      onDragState(true);
    },
    onMove: (_dx, _dy, e) => {
      setDrag({ x: e.clientX - grab.current.offset.x, y: e.clientY - grab.current.offset.y });
    },
    onEnd: (end) => {
      setDrag(null);
      onDragState(false);
      if (!end.moved) {
        if (ref.current !== null) onOpen(ref.current.getBoundingClientRect());
        return;
      }
      if (dropTargetAt(end.clientX, end.clientY, '[data-drop="saved"]') !== null) onSave();
    },
  });

  const style: CSSProperties =
    drag === null
      ? { width: `${String(CARD_W_REM)}rem` }
      : {
          position: "fixed",
          left: `${String(drag.x)}px`,
          top: `${String(drag.y)}px`,
          width: `${String(grab.current.width)}px`,
          height: `${String(grab.current.height)}px`,
          zIndex: DRAG_Z,
          transform: "none",
          cursor: "grabbing",
          // The card rides under the pointer; without this the drop's
          // hit-test finds the card itself, never the box beneath it.
          pointerEvents: "none",
        };

  return (
    <button
      ref={ref}
      type="button"
      data-case={entry.slug}
      aria-disabled={locked ? "true" : undefined}
      tabIndex={locked ? -1 : 0}
      onPointerDown={
        locked
          ? undefined
          : (e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              grab.current = { offset: { x: e.clientX - rect.left, y: e.clientY - rect.top }, width: rect.width, height: rect.height };
              startDrag(e);
            }
      }
      // The keyboard's door: Enter or Space synthesises a click with detail 0.
      onClick={
        locked
          ? undefined
          : (e) => {
              if (e.detail === 0) onOpen(e.currentTarget.getBoundingClientRect());
            }
      }
      className={[
        "pc-case-paper cursor-grab",
        drag === null ? "transition-transform duration-[var(--pc-dur)] ease-pc hover:-translate-y-1" : "",
        locked ? "cursor-default" : "",
      ].join(" ")}
      style={style}
    >
      <CaseKindRow words={caseKindWords(entry)} />
      <CasePaperCover slug={entry.slug} />
      <h3 className="pc-case-title">{entry.client}</h3>
      <CardRows entry={entry} />
      <p className="pc-case-byline m-0">{entry.tension}</p>
    </button>
  );
}

/** A card shrunk to a keepsake: the Continue and Saved boxes' form, on the same paper. */
function MiniCard({
  entry,
  kind,
  children,
  onOpen,
  onDragOut,
}: {
  entry: LibraryCase;
  /** The kind row's word: which box this keepsake lies in. */
  kind: string;
  children?: ReactNode;
  onOpen: (rect: DOMRect) => void;
  /** A drag that ends outside the box the card lives in. Undefined when the card cannot leave. */
  onDragOut?: ((clientX: number, clientY: number) => void) | undefined;
}): ReactNode {
  const [delta, setDelta] = useState<Point | null>(null);
  const ref = useRef<HTMLButtonElement | null>(null);
  const startDrag = usePointerDrag({
    onMove: (dx, dy) => {
      setDelta({ x: dx, y: dy });
    },
    onEnd: (end) => {
      setDelta(null);
      if (!end.moved) {
        if (ref.current !== null) onOpen(ref.current.getBoundingClientRect());
        return;
      }
      onDragOut?.(end.clientX, end.clientY);
    },
  });
  return (
    <button
      ref={ref}
      type="button"
      data-case={entry.slug}
      onPointerDown={startDrag}
      onClick={(e) => {
        if (e.detail === 0) onOpen(e.currentTarget.getBoundingClientRect());
      }}
      className={`pc-case-keepsake cursor-grab ${delta === null ? "transition-transform duration-[var(--pc-dur)] ease-pc hover:-translate-y-[2px]" : "cursor-grabbing"}`}
      style={delta === null ? undefined : { transform: `translate(${String(delta.x)}px, ${String(delta.y)}px)`, zIndex: DRAG_Z, position: "relative" }}
    >
      <CasePaperCover slug={entry.slug} />
      <span className="pc-case-keepsake-body">
        <CaseKindRow words={kind} />
        <span className="pc-case-keepsake-title">{entry.client}</span>
        {children}
      </span>
    </button>
  );
}

/** A finished engagement as a keepsake: the whole paper opens its casebook. */
function CompletedCard({ entry, engagement, onRead }: { entry: LibraryCase; engagement: LibraryCompletedEngagement; onRead: () => void }): ReactNode {
  return (
    <button type="button" data-case={entry.slug} data-session={engagement.sessionId} onClick={onRead} className="pc-case-keepsake pc-case-completed transition-transform duration-[var(--pc-dur)] ease-pc hover:-translate-y-[2px]">
      <CasePaperCover slug={entry.slug} />
      <span className="pc-case-keepsake-body">
        <CaseKindRow words={CASEBOOK.keepsakeKind} glyph="book" />
        <span className="pc-case-keepsake-title">{entry.client}</span>
        <span className="pc-case-completed-byline">{endingByline(engagement.ending, engagement.completedAt)}</span>
        <span className="pc-case-completed-door">{CASEBOOK.readTheBook}</span>
      </span>
    </button>
  );
}

function ContinueCard({ entry, engagement, onOpen, onContinue }: { entry: LibraryCase; engagement: LibraryOpenEngagement; onOpen: (rect: DOMRect) => void; onContinue: () => void }): ReactNode {
  return (
    <div className="flex flex-col gap-2">
      <MiniCard entry={entry} kind={LIBRARY.kindContinue} onOpen={onOpen}>
        <span className="mt-2 block">
          <Bar fraction={engagement.timeUsed} label={LIBRARY.timeBar(engagement.daysLeft)} tone="time" />
          <Bar fraction={engagement.evidenceFound} label={LIBRARY.evidenceBar} tone="evidence" />
        </span>
      </MiniCard>
      <button
        type="button"
        onClick={onContinue}
        className="cursor-pointer self-end border-0 bg-transparent p-0 font-sans text-micro text-ink hover:bg-marker"
      >
        {LIBRARY.walkBackIn}
      </button>
    </div>
  );
}

function ShelfRow({ shelf, onOpen, onSave, onDragState }: { shelf: Shelf<LibraryCase>; onOpen: (slug: string, rect: DOMRect) => void; onSave: (slug: string) => void; onDragState: (dragging: boolean) => void }): ReactNode {
  return (
    <section aria-label={shelf.title} data-shelf={shelf.id} aria-disabled={shelf.locked ? "true" : undefined} className="pc-library-shelf">
      <div className="pcs-heading">
        <h2 className="pcs-label m-0">{shelf.title}</h2>
      </div>
      <ul className="pc-shelf m-0 p-0">
        {shelf.cases.map((entry) => (
          <li key={entry.slug}>
            <LibraryCard
              entry={entry}
              locked={shelf.locked || shelf.lockedSlugs.includes(entry.slug)}
              onOpen={(rect) => {
                onOpen(entry.slug, rect);
              }}
              onSave={() => {
                onSave(entry.slug);
              }}
              onDragState={onDragState}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}

export function CaseLibrary({ cases, state, lagging, planDimensions, busy, onContinue, onStart, onReadPrologue, onSetSaved, onReadBook }: CaseLibraryProps): ReactNode {
  const [lightbox, setLightbox] = useState<LightboxState | null>(null);
  const [leaving, setLeaving] = useState<LightboxState | null>(null);
  const [dragging, setDragging] = useState(false);
  /** The "are you sure" is up over the lightbox. */
  const [confirmingStartOver, setConfirmingStartOver] = useState(false);
  const nonce = useRef(0);
  const savedBoxRef = useRef<HTMLDivElement | null>(null);

  const bySlug = useMemo(() => new Map(cases.map((c) => [c.slug, c])), [cases]);
  const shelves = useMemo(
    () => buildShelves({ cases, unlocked: state?.unlocked ?? false, calibrationOpen: state?.calibrationOpen ?? false, firstCase: state?.gate.firstCase ?? null, lagging, planDimensions, completedSlugs: state?.completed ?? [] }),
    [cases, state, lagging, planDimensions],
  );
  // The locked rows' sentence: what still opens them, in words, from the server's own gate.
  const lockedMessage = useMemo(() => {
    const gate = state?.gate ?? { firstCaseDone: false, signedIn: false, planAcknowledged: false };
    const first = state === null ? null : bySlug.get(state.gate.firstCase);
    return lockedMessageFor(gate, first?.client ?? "your first case");
  }, [state, bySlug]);
  const saved = useMemo(() => (state?.saved ?? []).flatMap((slug) => bySlug.get(slug) ?? []), [state, bySlug]);
  const openEngagements = useMemo(() => (state?.openEngagements ?? []).flatMap((e) => (bySlug.has(e.caseSlug) ? [e] : [])), [state, bySlug]);

  // A minimising lightbox leaves the tree once its exit has played; a
  // second opening inside that window simply replaces what is leaving.
  useEffect(() => {
    if (leaving === null) return;
    const t = setTimeout(() => {
      setLeaving(null);
    }, LEAVE_MS);
    return () => {
      clearTimeout(t);
    };
  }, [leaving]);

  const openLightbox = useCallback((slug: string, rect: DOMRect | null) => {
    nonce.current += 1;
    setConfirmingStartOver(false);
    setLightbox((current) => {
      if (current !== null && current.slug === slug) return current;
      if (current !== null) setLeaving(current);
      return { kind: "case", slug, pos: lightboxPositionFor(rect), nonce: nonce.current };
    });
  }, []);

  const closeLightbox = useCallback(() => {
    setConfirmingStartOver(false);
    setLightbox((current) => {
      if (current !== null) setLeaving(current);
      return null;
    });
  }, []);

  // Escape peels one layer: the confirm first, then the lightbox.
  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      if (confirmingStartOver) setConfirmingStartOver(false);
      else closeLightbox();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, [lightbox, confirmingStartOver, closeLightbox]);

  const savedSlugs = state?.saved ?? [];
  const save = useCallback(
    (slug: string) => {
      if (savedSlugs.includes(slug)) return;
      onSetSaved([...savedSlugs, slug]);
    },
    [savedSlugs, onSetSaved],
  );
  const unsave = useCallback(
    (slug: string) => {
      if (!savedSlugs.includes(slug)) return;
      onSetSaved(savedSlugs.filter((s) => s !== slug));
    },
    [savedSlugs, onSetSaved],
  );

  const lightboxEntry = lightbox === null ? null : (bySlug.get(lightbox.slug) ?? null);
  const leavingEntry = leaving === null || leaving.slug === lightbox?.slug ? null : (bySlug.get(leaving.slug) ?? null);
  const lockedShelves = shelves.filter((s) => s.locked);
  const openShelves = shelves.filter((s) => !s.locked);

  return (
    <main className="pc-sheet pc-library">
      <CoverSprites slugs={cases.map((c) => c.slug)} />
      <div className="pc-library-grid">
        {/* The left rail: what you were doing, and what you kept. */}
        <aside className="pc-library-rail">
          <section aria-label={LIBRARY.continueBox} className="pc-library-box">
            <div className="pcs-heading">
              <h2 className="pcs-label m-0">{LIBRARY.continueBox}</h2>
            </div>
            <div className="mt-5 flex flex-col gap-4">
              {openEngagements.map((engagement) => {
                const entry = bySlug.get(engagement.caseSlug);
                if (entry === undefined) return null;
                return (
                  <ContinueCard
                    key={engagement.sessionId}
                    entry={entry}
                    engagement={engagement}
                    onOpen={(rect) => {
                      openLightbox(entry.slug, rect);
                    }}
                    onContinue={() => {
                      onContinue(engagement.sessionId, engagement.caseSlug);
                    }}
                  />
                );
              })}
              {state !== null && openEngagements.length === 0 ? <p className="pcs-quiet m-0">{LIBRARY.continueEmpty}</p> : null}
            </div>
          </section>

          <section
            ref={savedBoxRef}
            aria-label={LIBRARY.savedBox}
            data-drop="saved"
            data-dragging={dragging ? "true" : undefined}
            className="pc-library-box pc-library-saved"
          >
            <div className="pcs-heading">
              <h2 className="pcs-label m-0">{LIBRARY.savedBox}</h2>
            </div>
            <div className="mt-5 flex flex-col gap-3">
              {saved.map((entry) => (
                <MiniCard
                  key={entry.slug}
                  entry={entry}
                  kind={LIBRARY.kindSaved}
                  onOpen={(rect) => {
                    openLightbox(entry.slug, rect);
                  }}
                  onDragOut={(x, y) => {
                    // Off the box means out of the box; inside it means put back down.
                    const box = savedBoxRef.current?.getBoundingClientRect();
                    const inside = box !== undefined && x >= box.left && x <= box.right && y >= box.top && y <= box.bottom;
                    if (!inside) unsave(entry.slug);
                  }}
                />
              ))}
              {state !== null && saved.length === 0 ? <p className="pcs-quiet m-0">{LIBRARY.savedEmpty}</p> : null}
            </div>
          </section>

          {/* THE COMPLETED BOX (2026-09-24, Andre): every finished engagement, newest first, each opening its casebook. */}
          <section aria-label={CASEBOOK.completedBox} className="pc-library-box pc-library-completed">
            <PanelHeading label={CASEBOOK.completedBox}>{CASEBOOK.completedNote}</PanelHeading>
            <div className="mt-5 flex flex-col gap-3">
              {(state?.completedEngagements ?? []).map((done) => {
                const entry = bySlug.get(done.caseSlug);
                if (entry === undefined) return null;
                return <CompletedCard key={done.sessionId} entry={entry} engagement={done} onRead={() => { onReadBook?.(done.sessionId); }} />;
              })}
              {state !== null && state.completedEngagements.length === 0 ? <p className="pcs-quiet m-0">{CASEBOOK.completedEmpty}</p> : null}
            </div>
          </section>
        </aside>

        {/* The shelves. */}
        <div className="pc-library-shelves">
          {openShelves.map((shelf) => (
            <ShelfRow key={shelf.id} shelf={shelf} onOpen={openLightbox} onSave={save} onDragState={setDragging} />
          ))}
          {lockedShelves.length > 0 ? (
            <div className="relative">
              <div className="pc-locked-shelves pc-library-locked" aria-hidden="true">
                {lockedShelves.map((shelf) => (
                  <ShelfRow key={shelf.id} shelf={shelf} onOpen={openLightbox} onSave={save} onDragState={setDragging} />
                ))}
              </div>
              <p className="pointer-events-none absolute inset-x-0 top-[12rem] m-0 mx-auto max-w-[36ch] text-center font-display text-lede font-bold text-ink">
                {lockedMessage}
              </p>
            </div>
          ) : null}
          {cases.length === 0 ? <p className="pcs-quiet m-0">No cases are published yet.</p> : null}
        </div>
      </div>

      {leaving !== null && leavingEntry !== null ? (
        <CaseLightbox
          key={`leaving-${String(leaving.nonce)}`}
          entry={leavingEntry}
          state={state}
          pos={leaving.pos}
          leaving
          busy={busy}
          saved={savedSlugs.includes(leavingEntry.slug)}
          onMove={() => undefined}
          onClose={() => undefined}
          onContinue={() => undefined}
          onStart={() => undefined}
          onStartOver={() => undefined}
          onReadPrologue={() => undefined}
          onToggleSaved={() => undefined}
        />
      ) : null}
      {lightbox !== null && lightboxEntry !== null ? (
        <CaseLightbox
          key={`open-${String(lightbox.nonce)}`}
          entry={lightboxEntry}
          state={state}
          pos={lightbox.pos}
          leaving={false}
          busy={busy}
          saved={savedSlugs.includes(lightboxEntry.slug)}
          onMove={(pos) => {
            setLightbox((current) => (current === null ? null : { ...current, pos }));
          }}
          onClose={closeLightbox}
          onContinue={onContinue}
          onStart={() => {
            onStart(lightboxEntry.slug, false);
          }}
          onStartOver={() => {
            setConfirmingStartOver(true);
          }}
          onReadPrologue={() => {
            onReadPrologue(lightboxEntry.slug);
          }}
          onToggleSaved={() => {
            if (savedSlugs.includes(lightboxEntry.slug)) unsave(lightboxEntry.slug);
            else save(lightboxEntry.slug);
          }}
        />
      ) : null}
      {confirmingStartOver && lightboxEntry !== null ? (
        <StartOverConfirm
          entry={lightboxEntry}
          busy={busy}
          onConfirm={() => {
            setConfirmingStartOver(false);
            onStart(lightboxEntry.slug, true);
          }}
          onCancel={() => {
            setConfirmingStartOver(false);
          }}
        />
      ) : null}
    </main>
  );
}

/** Exported for the drag tests: the pointer-down a card expects. */
export type CardPointerHandler = (e: ReactPointerEvent<HTMLElement>) => void;
